import { useEffect, useMemo, useState } from "react";
import { logWarn } from "@/lib/log";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Lock, ShieldCheck, Check, ArrowLeft, Clock, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, money, useSite } from "@/lib/site";
import { iconFor } from "@/components/site/common";
import { ReviewQuote } from "@/components/site/Reviews";
import NotFound from "./NotFound";

const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const PAYPAL_STYLE = { layout: "vertical", shape: "pill", label: "pay", height: 48 };
const inputCls = "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 outline-none focus:border-blue-500 transition-colors";

const PayPalMark = () => (
  <span className="font-display font-extrabold text-2xl tracking-tight italic"><span className="text-[#003087]">Pay</span><span className="text-[#009cde]">Pal</span></span>
);

const PayPalPending = ({ pkg }) => (
  <div className="text-center py-4" data-testid="checkout-paypal-pending">
    <PayPalMark />
    <p className="mt-5 flex items-center justify-center gap-1.5 font-display text-xl font-bold text-slate-900"><Clock className="w-5 h-5 text-[#009cde]" /> PayPal checkout is opening soon</p>
    <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">Our PayPal account is being activated. Want to get started now? Contact us and we'll send you a secure PayPal invoice for {pkg.name}.</p>
    <div className="mt-6 flex flex-wrap justify-center gap-3">
      <Link to={`/contact?service=${encodeURIComponent(pkg.name)}`} data-testid="checkout-paypal-contact-btn"
        className="inline-flex items-center gap-2 rounded-full bg-[#0070ba] hover:bg-[#005ea6] text-white px-6 py-3.5 font-semibold transition-colors">
        <MessageSquare className="w-4 h-4" /> Request a PayPal invoice
      </Link>
    </div>
    <p className="mt-4 text-xs text-slate-500">Or tap the chat bubble to ask us a question first.</p>
  </div>
);

const PayPalLive = ({ pkg, buyer, ready, clientId }) => {
  const navigate = useNavigate();
  const options = useMemo(() => ({ clientId, currency: "USD", intent: "capture" }), [clientId]);
  const rerenderKeys = useMemo(() => [pkg.id, buyer.name, buyer.email], [pkg.id, buyer.name, buyer.email]);
  return (
    <div data-testid="checkout-paypal-buttons" className={ready ? "" : "opacity-50 pointer-events-none"}>
      <PayPalScriptProvider options={options}>
        <PayPalButtons
          style={PAYPAL_STYLE}
          disabled={!ready}
          forceReRender={rerenderKeys}
          createOrder={async () => (await api.post("/paypal/orders", { package_id: pkg.id, ...buyer })).data.id}
          onApprove={async (data) => {
            const r = await api.post(`/paypal/orders/${data.orderID}/capture`);
            navigate(r.data.payment_status === "paid" ? `/payment/success?method=paypal&order_id=${data.orderID}` : "/payment/cancel");
          }}
          onCancel={() => navigate("/payment/cancel")}
          onError={() => toast.error("PayPal couldn't complete the payment. Please try again.")}
        />
      </PayPalScriptProvider>
    </div>
  );
};

const PayPalForm = ({ pkg, clientId }) => {
  const [buyer, setBuyer] = useState({ name: "", email: "" });
  const ready = buyer.name.trim().length > 0 && validEmail(buyer.email);
  return (
    <>
      <div>
        <p className="font-display font-bold text-lg">1. Your details</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <input placeholder="Full name" value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} className={inputCls} data-testid="checkout-name-input" />
          <input type="email" placeholder="Email for your receipt" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} className={inputCls} data-testid="checkout-email-input" />
        </div>
        {!ready && <p className="mt-2 text-xs text-slate-500" data-testid="checkout-hint">Enter your name and email to continue.</p>}
      </div>
      <div>
        <p className="font-display font-bold text-lg mb-4">2. Pay with PayPal</p>
        <PayPalLive pkg={pkg} buyer={buyer} ready={ready} clientId={clientId} />
      </div>
      <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400"><Lock className="w-3 h-3" /> Payments are processed securely by PayPal.</p>
    </>
  );
};

const usePayPalConfig = () => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    api.get("/paypal/config").then((r) => setCfg(r.data)).catch((e) => { logWarn("PayPal config failed", e?.message); setCfg({ enabled: false }); });
  }, []);
  return cfg;
};

const OrderSummary = ({ pkg }) => {
  const Icon = iconFor(pkg.id);
  return (
    <div className="rounded-3xl bg-[#090D16] text-white p-7 sm:p-8 grain relative overflow-hidden" data-testid="checkout-order-summary">
      <p className="overline !text-sky-400">Order summary</p>
      <div className="mt-6 flex items-start gap-4">
        <span className="grid place-items-center w-12 h-12 shrink-0 rounded-2xl bg-white/10 text-sky-400"><Icon className="w-5 h-5" /></span>
        <div><p className="font-display text-xl font-bold" data-testid="checkout-package-name">{pkg.name}</p><p className="text-sm text-slate-400">{pkg.tagline}</p></div>
      </div>
      <ul className="mt-6 space-y-2">
        {pkg.features.map((f) => <li key={f} className="flex gap-2.5 text-sm text-slate-300"><Check className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />{f}</li>)}
      </ul>
      <div className="mt-8 pt-6 border-t border-white/10 flex items-end justify-between">
        <span className="text-slate-400 text-sm">Total due today</span>
        <span className="font-display text-4xl font-extrabold" data-testid="checkout-price">{money(pkg.price)}<span className="text-sm text-slate-400 font-medium ml-1">USD</span></span>
      </div>
      {pkg.note && <p className="mt-3 text-xs text-slate-500">{pkg.note}</p>}
    </div>
  );
};

export default function Checkout() {
  const { id } = useParams();
  const { packages, loaded } = useSite();
  const cfg = usePayPalConfig();
  const pkg = packages.find((p) => p.id === id);
  if (!loaded) return <div className="min-h-screen" />;
  if (!pkg) return <NotFound />;
  return (
    <section className="grid-lines min-h-screen" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-32 pb-20">
        <Link to="/pricing" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-blue-600" data-testid="checkout-back-link"><ArrowLeft className="w-4 h-4" /> Back to pricing</Link>
        <h1 className="mt-6 text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Checkout</h1>
        <div className="mt-10 grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7 space-y-6 rounded-3xl bg-white border border-slate-200 p-6 sm:p-8">
            {!cfg && <div className="h-48 rounded-2xl bg-slate-100 animate-pulse" />}
            {cfg && (cfg.enabled ? <PayPalForm pkg={pkg} clientId={cfg.client_id} /> : <PayPalPending pkg={pkg} />)}
          </div>
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <OrderSummary pkg={pkg} />
            <p className="mt-4 flex items-center gap-2 text-sm text-slate-600"><ShieldCheck className="w-4 h-4 text-emerald-500" /> We'll email you within one business day to book your kickoff.</p>
            <ReviewQuote serviceId={pkg.id} />
          </div>
        </div>
      </div>
    </section>
  );
}
